// Add this to your existing dateUtils.js

/**
 * For sidebar display: shows time if today, date otherwise
 */
export const formatSidebarTime = (date) => {
  if (!date) return "";

  const messageDate = new Date(date);
  if (isNaN(messageDate)) return "";

  const now = new Date();

  const isToday =
    messageDate.getDate() === now.getDate() &&
    messageDate.getMonth() === now.getMonth() &&
    messageDate.getFullYear() === now.getFullYear();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();

  const isSameYear =
    messageDate.getFullYear() === now.getFullYear();

  // If message is today → show time
  if (isToday) {
    return messageDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Yesterday
  if (isYesterday) {
    return "Yesterday";
  }
  // If same year → show day + month
  if (isSameYear) {
    return messageDate.toLocaleDateString([], {
      day: "2-digit",
      month: "short",
    });
  }

  // If previous year → show day + month + year
  return messageDate.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
/**
 * Converts a Twilio UTC date to the user's local timezone
 * Twilio returns Date objects or ISO strings — both are UTC
 */
export const toLocalDate = (twilioDate) => {
  if (!twilioDate) return null;
  // If it's already a Date object, JS automatically handles UTC → local
  // If it's a string, new Date() parses it as UTC
  return new Date(twilioDate);
};

export const formatMessageTime = (twilioDate) => {
  const date = toLocalDate(twilioDate);
  if (!date || isNaN(date)) return "";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // change to false for 24hr
  });
};

export const formatMessageDate = (twilioDate) => {
  const date = toLocalDate(twilioDate);
  if (!date || isNaN(date)) return "";

  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const isToday = (twilioDate) => {
  const date = toLocalDate(twilioDate);
  const now = new Date();
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

export const isYesterday = (twilioDate) => {
  const date = toLocalDate(twilioDate);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

export const isNewDay = (dateA, dateB) => {
  const a = toLocalDate(dateA);
  const b = toLocalDate(dateB);
  return (
    a.getDate() !== b.getDate() ||
    a.getMonth() !== b.getMonth() ||
    a.getFullYear() !== b.getFullYear()
  );
};

/**
 * Stripe billing calendar dates (YYYY-MM-DD) use UTC calendar days.
 * Payments are scheduled at 12:00 UTC regardless of the coach's local timezone.
 */

/** Today's UTC calendar date as YYYY-MM-DD */
export const getStripeUtcTodayString = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Min selectable date for react-datepicker — UTC "today" shown as a calendar day everywhere */
export const getStripeUtcMinDate = () => {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

/** Parse YYYY-MM-DD for display in a date picker (avoids UTC-midnight off-by-one) */
export const parseStripeCalendarDate = (dateString) => {
  if (!dateString) return null;
  const [y, m, d] = dateString.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/** Format a date picker selection as YYYY-MM-DD (calendar day the user clicked) */
export const formatStripeCalendarDate = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** True when dateString is today or later on the UTC calendar (Stripe platform time) */
export const isStripeUtcTodayOrFuture = (dateString) => {
  if (!dateString) return false;
  return dateString >= getStripeUtcTodayString();
};

/** Parse API date values (YYYY-MM-DD or ISO string) for date picker display */
export const parseStripeCalendarDateFromApi = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate()
    );
  }
  if (typeof dateValue === "string") {
    return parseStripeCalendarDate(dateValue.split("T")[0]);
  }
  return null;
};

/** True when the date is strictly after UTC today (calendar day) */
export const isStripeUtcFutureDate = (dateValue) => {
  if (!dateValue) return false;
  const datePart =
    typeof dateValue === "string"
      ? dateValue.split("T")[0]
      : formatStripeCalendarDate(dateValue);
  return datePart > getStripeUtcTodayString();
};

/** Min selectable date: UTC tomorrow */
export const getStripeUtcTomorrowMinDate = () => {
  const today = getStripeUtcMinDate();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
};

/** Min selectable date: UTC today + 2 days */
export const getStripeUtcDayAfterTomorrowMinDate = () => {
  const today = getStripeUtcMinDate();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2);
};

/** Min selectable date: first day of next month on the UTC calendar */
export const getStripeUtcFirstOfNextMonthMinDate = () => {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
};

/** Compare calendar dates (Date or YYYY-MM-DD) on or after a minimum */
export const isStripeUtcDateOnOrAfter = (date, minDate) => {
  const dateStr =
    typeof date === "string"
      ? date.split("T")[0]
      : formatStripeCalendarDate(date);
  const minStr =
    typeof minDate === "string"
      ? minDate.split("T")[0]
      : formatStripeCalendarDate(minDate);
  return dateStr >= minStr;
};

/** Compare calendar dates (Date or YYYY-MM-DD) on or before a maximum */
export const isStripeUtcDateOnOrBefore = (date, maxDate) => {
  const dateStr =
    typeof date === "string"
      ? date.split("T")[0]
      : formatStripeCalendarDate(date);
  const maxStr =
    typeof maxDate === "string"
      ? maxDate.split("T")[0]
      : formatStripeCalendarDate(maxDate);
  return dateStr <= maxStr;
};

/** Add days to a calendar date without timezone drift */
export const addStripeCalendarDays = (date, days) => {
  const parsed =
    date instanceof Date ? date : parseStripeCalendarDateFromApi(date);
  if (!parsed) return null;
  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate() + days
  );
};

/**
 * Minimum selectable one-time fee date. Stripe finalizes at 12:00 UTC on the selected day;
 * the backend skips dates whose finalize time is not at least ~5 minutes in the future.
 */
export const getStripeOneTimeFeeMinDate = () => {
  const now = new Date();
  const utcToday = getStripeUtcMinDate();
  const noonTodayUtcMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    12,
    0,
    0,
    0
  );
  const cutoffMs = now.getTime() + 5 * 60 * 1000;
  if (noonTodayUtcMs > cutoffMs) {
    return utcToday;
  }
  return getStripeUtcTomorrowMinDate();
};